import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { QueryState } from "@/components/common/AsyncState";
import { fetchRolePermissions, resetRolePermissionsToDefault, updateRolePermissions } from "@/api/settings";
import { ROLE_LABELS } from "@/types/enums";
import type { Role } from "@/types/enums";
import { PERMISSION_GROUPS, PERMISSION_LABELS } from "@/types/permissions";
import type { PermissionKey } from "@/types/permissions";

const ROLES: Role[] = ["DIRECTOR", "ADMIN", "ACCOUNTANT", "TEACHER", "STUDENT"];

export function RolesMatrixTab() {
  const queryClient = useQueryClient();
  const [role, setRole] = useState<Role>("ADMIN");
  const [matrix, setMatrix] = useState<Partial<Record<PermissionKey, boolean>>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["role-permissions", role],
    queryFn: () => fetchRolePermissions(role),
  });

  useEffect(() => {
    if (data) {
      const next: Partial<Record<PermissionKey, boolean>> = {};
      for (const row of data) next[row.permissionKey] = row.allowed;
      setMatrix(next);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => updateRolePermissions(role, matrix),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["role-permissions", role] }),
  });

  const resetMutation = useMutation({
    mutationFn: () => resetRolePermissionsToDefault(role),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["role-permissions", role] }),
  });

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField select label="Роль" size="small" value={role} onChange={(e) => setRole(e.target.value as Role)} sx={{ minWidth: 220 }}>
          {ROLES.map((r) => (
            <MenuItem key={r} value={r}>
              {ROLE_LABELS[r]}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RestartAltIcon />} disabled={resetMutation.isPending} onClick={() => resetMutation.mutate()}>
            Сбросить к рекомендованным
          </Button>
          <Button variant="contained" disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            Сохранить
          </Button>
        </Stack>
      </Stack>

      <QueryState isLoading={isLoading} error={error}>
        <Stack spacing={2}>
          {PERMISSION_GROUPS.map((group) => (
            <Paper key={group.title} variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                {group.title}
              </Typography>
              <Grid container spacing={1}>
                {group.keys.map((key) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{PERMISSION_LABELS[key]}</Typography>
                      <Switch
                        checked={Boolean(matrix[key])}
                        onChange={(e) => setMatrix((prev) => ({ ...prev, [key]: e.target.checked }))}
                      />
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ))}
        </Stack>
      </QueryState>
    </>
  );
}
